import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { MessageComposer, MessagePostcard } from "@/components/MessageBoard";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail } from "lucide-react";

export default function AdminMessages() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/messages${isAdmin ? "?include_expired=true" : ""}`);
      setMessages(data);
    } catch (e) {
      toast.error("could not load messages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin]);

  const onPosted = (msg) => setMessages((prev) => [msg, ...prev]);

  const onDelete = async (m) => {
    if (!window.confirm(`remove this postcard?`)) return;
    try {
      await api.delete(`/messages/${m.id}`);
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("removed.");
    } catch { toast.error("could not remove."); }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-messages" className="max-w-4xl">
        <span className="editorial-label">studio · the message board</span>
        <h1 className="font-serif text-4xl sm:text-5xl lowercase text-charcoal mt-3 mb-2 flex items-center gap-3"><Mail className="w-7 h-7 text-brass" /> postcards from the studio.</h1>
        <p className="text-espresso max-w-2xl mb-10">leave a note for the team — daily mood, urgent updates, a thank-you. every postcard pops up once when the team next signs in.</p>

        {isAdmin && (
          <div className="mb-10">
            <MessageComposer onPosted={onPosted} />
          </div>
        )}

        <div className="space-y-5" data-testid="messages-list">
          {loading && <p className="text-xs text-taupe">loading…</p>}
          {!loading && messages.length === 0 && (
            <div className="border border-dashed border-taupe/50 p-10 text-center bg-linen/50">
              <p className="font-serif text-2xl lowercase text-taupe">no postcards yet.</p>
            </div>
          )}
          {messages.map((m) => (
            <MessagePostcard
              key={m.id}
              message={m}
              canDelete={isAdmin}
              onDelete={onDelete}
            />
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
